'use client'
import * as React from 'react'
import { InputLabel, ListItem, Stack } from '@mui/material'
import { JSX } from 'react'
import { FieldValue, FormFieldType } from '@/types'
import { Dispatch, SetStateAction, memo, useCallback } from 'react'
import { BORDER_GREY, PRIMARY, PRIMARY_LIGHT, WHITE } from '@/constants/colors'
import { FieldType } from '@/enum'
import { DragIndicator } from '@mui/icons-material'
import { Draggable, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd'
import { IconButton } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useTranslations } from 'next-intl'
import TextField from './fields/TextField'
import DateField from './fields/DateField'
import TimeField from './fields/TimeField'
import SelectField from './fields/SelectField'
import MultiSelectField from './fields/MultiSelectField'
import AutoCompleteTextField from '../shared/AutoCompleteTextField'
import AutoCompleteDropdown from '../shared/AutoCompleteDropdown'
import FieldControls from './controls/FieldControls'

type SingleFieldProps = {
  id: string
  index: number
  label: string
  value: FieldValue
  type: FieldType
  isDefault: boolean
  isHidden: boolean
  identifier: string
  options?: string[]
  placeholder?: string
  setFields: Dispatch<SetStateAction<FormFieldType[]>>
}

const SingleField: React.FC<SingleFieldProps> = ({
  id,
  index,
  label,
  value,
  type,
  isDefault,
  isHidden,
  identifier,
  options,
  placeholder,
  setFields,
}) => {
  const t = useTranslations('HOMEPAGE')

  const onChangeHandler = useCallback((val: FieldValue, fieldId: string, isLabel?: boolean) => {
    setFields((prev) =>
      prev.map((field) => {
        if (field.id !== fieldId) return field

        const updatedField = { ...field }
        if (isLabel && typeof val === 'string') {
          updatedField.label = val
        } else {
          updatedField.value = val
        }
        return updatedField
      }),
    )
  }, [])

  const visibilityAndDeleteIconClickHandler = useCallback((id: string, label: string, isDelete: boolean) => {
    setFields((prev) => {
      if (isDelete) {
        return prev.filter((field) => field.id !== id)
      }
      return prev.map((field) => {
        if (field.id === id && field.isDefault) {
          if (!field.isHidden) {
            enqueueSnackbar(`${label} ${t('HIDE_FIELD')}`, {
              variant: 'default',
              TransitionProps: { direction: 'up' },
            })
          }
          return {
            ...field,
            isHidden: !field.isHidden,
          }
        }
        return field
      })
    })
  }, [])

  const renderField = useCallback(() => {
    const commonProps = {
      id,
      value,
      isHidden,
      onChangeHandler,
    }

    switch (type) {
      case FieldType.TEXT_FIELD:
        return <TextField {...commonProps} placeholder={placeholder} isLabel={false} />
      case FieldType.DATE:
        return <DateField {...commonProps} />
      case FieldType.TIME:
        return <TimeField {...commonProps} />
      case FieldType.SELECT:
        if (!options) return null
        return <SelectField {...commonProps} options={options} />
      case FieldType.AUTOCOMPLETE:
        return <AutoCompleteTextField {...commonProps} identifier={identifier} placeholder={placeholder} />
      case FieldType.AUTOCOMPLETE_DROPDOWN:
        return <AutoCompleteDropdown {...commonProps} placeholder={placeholder} options={options} />
      case FieldType.MULTI_SELECT:
        if (!options) return null
        return <MultiSelectField {...commonProps} options={options} placeholder={placeholder} />
      default:
        return <TextField {...commonProps} placeholder={placeholder} isLabel={false} />
    }
  }, [type, id, value, isHidden, identifier, options, placeholder])

  return (
    <Draggable key={id} draggableId={id} index={index}>
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
        <ListItem disablePadding ref={provided.innerRef} {...provided.draggableProps}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 2,
              mb: { xs: 3, sm: 3 },
              width: '100%'
            }}>
            <Stack sx={{
              gap: 1
            }}>
              <IconButton
                size="small"
                color="primary"
                sx={{
                  border: `1px solid ${BORDER_GREY}`,
                  cursor: 'grab',
                  flexShrink: 0,
                }}
                disabled={isHidden}
                {...provided.dragHandleProps}
              >
                <DragIndicator fontSize="small" />
              </IconButton>
              <FieldControls
                id={id}
                label={label}
                isDefault={isDefault}
                isHidden={isHidden}
                isDesktop={false}
                onControlClick={visibilityAndDeleteIconClickHandler}
              />
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{
                alignItems: {
                  xs: 'flex-start',
                  sm: 'center',
                },

                gap: 2,
                overflow: 'hidden',
                width: '100%',

                border: {
                  xs: `1px solid ${snapshot.isDragging ? PRIMARY : BORDER_GREY}`,
                  sm: 'none',
                },

                backgroundColor: {
                  xs: snapshot.isDragging ? PRIMARY_LIGHT : WHITE,
                  sm: WHITE,
                },

                borderRadius: '4px',
                p: { xs: 2, sm: 0 },
                opacity: isHidden ? 0.3 : 1,
                pointerEvents: isHidden ? 'none' : 'default'
              }}>
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  width: { xs: '100%', sm: '30%' }
                }}>
                {isDefault ? (
                  <InputLabel
                    sx={{
                      color: snapshot.isDragging ? PRIMARY : 'initial',
                      fontWeight: snapshot.isDragging ? 500 : 'initial',
                    }}
                  >
                    {label}
                  </InputLabel>
                ) : (
                  <TextField
                    id={id}
                    value={label}
                    isHidden={isHidden}
                    placeholder={t('NEW_FIELD_NAME_PLACEHOLDER')}
                    onChangeHandler={onChangeHandler}
                    isLabel={true}
                  />
                )}
              </Stack>
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  width: { xs: '100%', sm: '70%' }
                }}>
                {renderField()}
              </Stack>
            </Stack>
            <FieldControls
              id={id}
              label={label}
              isDefault={isDefault}
              isHidden={isHidden}
              isDesktop={true}
              onControlClick={visibilityAndDeleteIconClickHandler}
            />
          </Stack>
        </ListItem>
      )}
    </Draggable>
  );
}

export default memo(SingleField)
